import {createEntityAdapter, createSlice} from "@reduxjs/toolkit"
import {StoreState} from "store"
import {useSelector} from "react-redux"
import {Application} from "types/Application"
import {fetchApplications, FetchApplicationsParams} from "./fetchApplications"
import {assembleMenuApplication} from "./assembleMenuApplication"
import {rejectApplication} from "./rejectApplication"
import {fetchApplicationById} from "./fetchApplicationById"
import {approveApplication} from "./approveApplication"

export const applicationAdapter = createEntityAdapter<Application>()

export interface StateProps {
    loading: boolean
    // id актуального запроса списка — ответы отменённых и устаревших запросов игнорируются
    requestId?: string
    // Параметры последнего запроса списка (фильтры формы и таблицы, сортировка, пагинация).
    // Нужны для перезапроса из мест, где нет доступа к состоянию таблицы — например,
    // после принятия/отказа отчёта, чтобы не сбрасывать выбранные фильтры.
    query?: FetchApplicationsParams
    pagination: {
        total: number
    }
    more: {
        loading: boolean
        details?: Application
    }
}

const initialState = applicationAdapter.getInitialState<StateProps>({
    loading: true,
    requestId: undefined,
    query: undefined,
    pagination: {
        total: 0
    },
    more: {
        loading: false,
        details: undefined
    }
})

const applicationSlice = createSlice({
    name: "application",
    initialState,
    reducers: {},
    extraReducers: builder => {
        // Вывод всех заявок
        builder.addCase(fetchApplications.pending, (state, action) => {
            state.loading = true
            state.requestId = action.meta.requestId
            state.query = action.meta.arg
        })
        builder.addCase(fetchApplications.fulfilled, (state, action) => {
            // Ответ устаревшего запроса не должен затирать данные актуального
            if (state.requestId !== action.meta.requestId) return
            applicationAdapter.removeAll(state)
            applicationAdapter.setMany(state, action.payload?.data?.content)
            state.pagination.total = action.payload?.data?.total_elements
            state.loading = false
            state.requestId = undefined
        })
        builder.addCase(fetchApplications.rejected, (state, action) => {
            // Отменённый запрос не снимает загрузку с запроса, который его вытеснил
            if (state.requestId !== action.meta.requestId) return
            state.loading = false
            state.requestId = undefined
        })
        // Разрешение заявки
        builder.addCase(assembleMenuApplication.fulfilled, (state, action) => {
            applicationAdapter.setOne(state, action.payload.application)
        })
        // Вывод заявки по id
        builder.addCase(fetchApplicationById.pending, state => {
            state.more.loading = true
        })
        builder.addCase(fetchApplicationById.fulfilled, (state, action) => {
            state.more.loading = false
            state.more.details = action.payload.application
        })
        builder.addCase(fetchApplicationById.rejected, state => {
            state.more.loading = false
        })
        //
        builder.addCase(approveApplication.pending, state => {
            state.loading = true
        })
        builder.addCase(approveApplication.fulfilled, state => {
            state.loading = false
        })
        builder.addCase(approveApplication.rejected, state => {
            state.loading = false
        })

        //
        builder.addCase(rejectApplication.pending, state => {
            state.loading = true
        })
        builder.addCase(rejectApplication.fulfilled, state => {
            state.loading = false
        })
        builder.addCase(rejectApplication.rejected, state => {
            state.loading = false
        })
    }
})

export const {selectById, selectAll} = applicationAdapter.getSelectors<StoreState>(state => state.application)

// Загрузка заявок (списком)
export const useLoadingApplications = () => useSelector((state: StoreState) => state.application.loading)
// Вывод всех заявок
export const useSelectAllApplications = () => useSelector(selectAll)
// Пагинация
export const useSelectPaginationApplications = () =>
    useSelector((state: StoreState) => state.application.pagination.total)
// Параметры последнего запроса списка — для перезапроса с теми же фильтрами
export const useSelectQueryApplications = () => useSelector((state: StoreState) => state.application.query)
// Вывод информацию о заявке
export const useSelectDetailsApplication = () => useSelector((state: StoreState) => state.application.more)

export default applicationSlice.reducer
